import React, { useCallback, useMemo, useState } from 'react';
import { IItemForGetShortInfo } from 'types';
import { Checkbox } from 'antd';
import Text from 'components/Text/Text';
import SearchField from 'components/SearchField/SearchField';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';
import style from './MultipleSelect.module.scss';

interface IProps {
	data: IItemForGetShortInfo[];
	isLoading: boolean;
	refetch: () => void;
	responseError: boolean;
	setModifierGroups: (value: string[]) => void;
	modifierGroups: string[];
}

export const MultipleSelect: React.FC<IProps> = React.memo(
	({ data, isLoading, refetch, responseError, setModifierGroups, modifierGroups }) => {
		const [searchString, setSearchString] = useState<string>('');

		const filteredModifierGroups = useMemo(() => {
			return searchString
				? data.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
				: data;
		}, [searchString, data]);

		const handleSelectAllModifierGroups = useCallback(() => {
			if (data.length === modifierGroups.length) {
				setModifierGroups([]);
			} else {
				const IDsOfAllModifierGroups = data.map((el) => el.id);
				setModifierGroups(IDsOfAllModifierGroups);
			}
		}, [setModifierGroups, data, modifierGroups]);

		const renderSelectMenu = useCallback(
			(menu: React.ReactElement) => {
				const getIsCheckedByHasAccess = () => {
					return !!(data.length && data.length === modifierGroups.length);
				};

				return (
					<>
						<div className={style.selectMenuHeader}>
							<Checkbox
								className={style.checkBox}
								checked={getIsCheckedByHasAccess()}
								disabled={!data.length}
								onChange={handleSelectAllModifierGroups}>
								<Text
									translationNameSpace="form"
									translationKey="selectAllModifierGroupsLabel"
									asFragment={true}
								/>
							</Checkbox>
							<SearchField
								onChangeSelectSearchString={setSearchString}
								searchString={searchString}
								className={style.searchField}
							/>
						</div>
						{menu}
					</>
				);
			},
			[data, modifierGroups, handleSelectAllModifierGroups, setSearchString, searchString],
		);

		return (
			<OutsideFormSelect
				testId="MultipleSelect"
				name="modifierGroups"
				label="modifierGroupLabel"
				value={modifierGroups}
				options={filteredModifierGroups}
				fetchError={responseError}
				loading={isLoading}
				onRefetch={refetch}
				disabled={isLoading}
				onChange={setModifierGroups}
				counterValue={modifierGroups.length}
				withValidation={false}
				dropdownRender={renderSelectMenu}
				mode="multiple"
			/>
		);
	},
);
